/**
 * Sign-in screen. Authenticates with SRP via the auth context, then routes to
 * the dashboard. Errors from Cognito surface inline.
 */
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Field, Notification } from '@nuff/ui/react';

import { useAuth } from '../auth/context';

export function SignIn(): React.JSX.Element {
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'signedIn') {
    return <Navigate to="/app" replace />;
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      await navigate('/app');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not sign in.');
      setSubmitting(false);
    }
  }

  return (
    <section className="auth" aria-labelledby="signin-title">
      <h1 id="signin-title" className="auth__title">
        Sign in
      </h1>
      <p className="auth__lead">Welcome back. Sign in to manage your newsletters.</p>
      <form className="auth__form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && <Notification variant="error">{error}</Notification>}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={setEmail}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={setPassword}
        />
        <Button className="app__btn-start" type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
      <p className="auth__alt">
        <Link to="/reset">Forgot password?</Link>
      </p>
      <p className="auth__alt">
        New here? <Link to="/signup">Create an account</Link>
      </p>
    </section>
  );
}
